import {
  InfiniteData,
  Mutation,
  QueryClient,
  useQueryClient,
} from '@mint-lib/query-client';
import { produce } from 'immer';

import * as API from '../../api/index.js';
import type {
  AsyncValidationState,
  DataFilePreview,
  TableDataFileValidatedRowsResponse,
  TotalErrorsCountData,
  ValidationSearchParams,
} from '../../api/types.js';
import { getPaginatedDataPlaceholder } from '../../constants/tablesConfig.js';
import { useNativeMetricsPlatformMediaRow } from '../../hooks/ff/useNativeMetricsPlatformMediaRow.js';
import {
  EnhancedMutationExtraOptions,
  useEnhancedMutation,
} from '../../hooks/useEnhancedMutation.js';
import { useTypedInfiniteQuery } from '../../hooks/useTypedInfiniteQuery.js';
import { useTypedQuery } from '../../hooks/useTypedQuery.js';
import {
  initialSearchParams,
  MutationKeys,
  QueryKeys,
} from '../queryClient.js';
import { useDataFilePreviewQueryOptions } from './preview.js';

type MutationPartial<TVariables, TContext> = {
  /**  partial mutation type with options and state */
  options: Mutation<unknown, unknown, TVariables, TContext>['options'] &
    EnhancedMutationExtraOptions & { meta: { key: string } };
  state: Mutation<unknown, unknown, TVariables, TContext>['state'];
};

export const useTableDataFileTotalErrorsCount = (
  dataFileId: string,
  options?: { enabled?: boolean; throwOnError?: boolean },
) => {
  const isNativeMetricsFFEnabled = useNativeMetricsPlatformMediaRow();

  return useTypedQuery({
    queryKey: QueryKeys.dataFile.steps.validation.totalErrorsCount(dataFileId),
    queryFn: () =>
      isNativeMetricsFFEnabled
        ? API.getTableDataFileValidatedRowsSummary(dataFileId)
        : API.getTableDataFileTotalErrorsCount(dataFileId),
    ...options,
  });
};

export const useTableDataFileValidatedRows = (
  dataFileId: string,
  searchParams: ValidationSearchParams,
  options?: { enabled?: boolean; throwOnError?: boolean },
) => {
  const queryKey = QueryKeys.dataFile.steps.validation.validatedRows(
    dataFileId,
    searchParams,
  );

  return useTypedInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1 }) =>
      API.getTableDataFileValidatedRows(dataFileId, {
        ...searchParams,
        pageNumber: pageParam || initialSearchParams.pageNumber,
      }),
    initialPageParam: initialSearchParams.pageNumber,
    getNextPageParam: (lastPage) => {
      if (lastPage.pageNumber * lastPage.itemsPerPage >= lastPage.totalItems) {
        return undefined;
      }

      return lastPage.pageNumber + 1;
    },
    placeholderData: {
      pages: [getPaginatedDataPlaceholder({ headers: [] })],
      pageParams: [],
    },
    ...options,
  });
};

export type EditCellMutation = MutationPartial<
  EditCellMutationVariables,
  EditCellMutationContext
>;
export type EditCellMutationVariables = Parameters<typeof API.editField>[0] & {
  meta: { rowIndex: number; colIndex: number };
};

type EditCellMutationContext = {
  previousPreview?: DataFilePreview;
  previousErrorsCount?: TotalErrorsCountData;
  previousValidatedRows?: InfiniteData<TableDataFileValidatedRowsResponse>;
};

export const getEditDataFileFieldOptions = ({
  queryClient,
  dataFileId,
  searchParams,
  previewQueryKey,
}: {
  queryClient: QueryClient;
  dataFileId: string;
  searchParams: ValidationSearchParams;
  previewQueryKey: readonly unknown[];
}) => {
  const totalErrorsCountQueryKey =
    QueryKeys.dataFile.steps.validation.totalErrorsCount(dataFileId);
  const validatedRowsBaseQueryKey =
    QueryKeys.dataFile.steps.validation.validatedRows(dataFileId);
  const dynamicValidatedRowsQueryKey = [
    ...validatedRowsBaseQueryKey,
    searchParams,
  ];

  const mutationOptions: EditCellMutation['options'] = {
    mutationFn: API.editField,
    onMutate: ({ payload, meta }: EditCellMutationVariables) => {
      queryClient.cancelQueries({ queryKey: previewQueryKey });
      queryClient.cancelQueries({ queryKey: totalErrorsCountQueryKey });
      queryClient.cancelQueries({
        queryKey: validatedRowsBaseQueryKey,
        exact: false,
      });

      const previousPreview =
        queryClient.getQueryData<DataFilePreview>(previewQueryKey);
      const previousErrorsCount =
        queryClient.getQueryData<TotalErrorsCountData>(
          totalErrorsCountQueryKey,
        );
      const previousValidatedRows = queryClient.getQueryData<
        InfiniteData<TableDataFileValidatedRowsResponse>
      >(dynamicValidatedRowsQueryKey);

      let isInvalidCell = false;

      if (previousPreview && previousValidatedRows) {
        const newValidatedRows = produce(previousValidatedRows, (draft) => {
          const pageNumber = Math.floor(
            meta.rowIndex / searchParams.itemsPerPage,
          );
          const pageRowIndex = meta.rowIndex % searchParams.itemsPerPage;
          const page = draft.pages[pageNumber];
          const row = page.items[pageRowIndex];
          const cell = row.validatedCells[meta.colIndex];
          isInvalidCell = Boolean(cell.error);

          cell.rawCell = payload.rawValue;
          cell.isEdited = true;
          cell.error = null;
        });
        queryClient.setQueryData(previewQueryKey, {
          ...previousPreview,
          isEdited: true,
        });
        if (isInvalidCell) {
          queryClient.setQueryData<TotalErrorsCountData>(
            totalErrorsCountQueryKey,
            (prev) => ({
              ...prev,
              totalErrors: (prev?.totalErrors ?? 1) - 1,
            }),
          );
        }
        queryClient.setQueryData(
          dynamicValidatedRowsQueryKey,
          newValidatedRows,
        );
      }

      queryClient.removeQueries({
        queryKey: validatedRowsBaseQueryKey,
        type: 'inactive',
        exact: false,
      });

      return {
        previousPreview,
        previousErrorsCount,
        previousValidatedRows,
      };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(previewQueryKey, context?.previousPreview);
      queryClient.setQueryData(
        totalErrorsCountQueryKey,
        context?.previousErrorsCount,
      );
      queryClient.setQueryData(
        dynamicValidatedRowsQueryKey,
        context?.previousValidatedRows,
      );
    },
    meta: {
      key: MutationKeys.editDataTableField,
      getErrorsMap: (t) => ({
        default: t('Failed to edit field'),
        NotAllowedCharactersError: t(
          'Only Latin characters, numbers and special symbols are allowed',
        ),
      }),
    },
  };

  return mutationOptions;
};

export const useEditTableDataFileField = (
  dataFileId: string,
  searchParams: ValidationSearchParams,
) => {
  const queryClient = useQueryClient();

  const { queryKey: previewQueryKey } = useDataFilePreviewQueryOptions({
    urlParams: { id: dataFileId },
  });

  const mutationOptions = getEditDataFileFieldOptions({
    queryClient,
    dataFileId,
    searchParams,
    previewQueryKey,
  });

  return useEnhancedMutation(mutationOptions);
};

export const useAsyncValidationState = (dataFileId: string) => {
  const validationStateQueryKey =
    QueryKeys.dataFile.validationState(dataFileId);

  const { data: validationState } = useTypedQuery({
    queryKey: validationStateQueryKey,
    queryFn: () => null as unknown as AsyncValidationState,
    enabled: false,
  });

  return validationState;
};
